import { useEffect } from 'react';

import type { ProductKeyWithTimestampMap } from '../../common/constants/3p-bulk-connect';
import { ProductKeys3P } from '../../common/constants/3p-product-configs';
import { is3pProductKey } from '../../common/constants/products';
import { isProductSmartlink } from '../../common/utils/connectors';
import { useBulkConnectLocalStorage } from '../../common/utils/use-bulk-connect-local-storage';
import {
	useBulkConnectActions,
	useIsBulkConnectModalOpen,
	useProductConnectedInSession,
	useProductOrder,
	useProductStatuses,
} from '../store/3p-bulk-connect';
import { useBootstrap } from '../store/bootstrap';

export const isProductKeyWithTimestampMap = (value: any): value is ProductKeyWithTimestampMap => {
	if (!value || typeof value !== 'object') {
		return false;
	}

	return Object.entries(value).every(
		([key, timestamp]) =>
			is3pProductKey(key) && typeof timestamp === 'string' && !isNaN(parseInt(timestamp, 10)),
	);
};

export const useNewlyConnectedProducts = () => {
	const [storedState] = useBulkConnectLocalStorage();
	const [{ thirdPartyConfigs }] = useBootstrap();

	const getNewlyConnectedProducts = (expiryDuration: number = Infinity) => {
		const currentTime = Date.now();
		const previous3pProducts: ProductKeyWithTimestampMap = isProductKeyWithTimestampMap(
			storedState?.previous3pProducts,
		)
			? storedState.previous3pProducts
			: {};

		const entries: [ProductKeys3P, number][] = Object.entries(previous3pProducts).map(
			([key, timestamp]) => [key as ProductKeys3P, parseInt(timestamp, 10)],
		);
		return entries
			.filter(([productKey, timestamp]) => {
				const productConfig = thirdPartyConfigs?.[productKey];
				if (!productConfig) {
					return false;
				}
				return (
					currentTime - timestamp < expiryDuration && // check if the product was noticed within the expiryDuration
					productConfig.userNeedsOAuth && // check if the user needs to OAuth
					!isProductSmartlink({ productKey, thirdPartyConfigs }) && // make sure the connector is not a smartlink type
					!productConfig.isFederated && // make sure the connector is not federated type
					productConfig.isEnabled
				);
			})
			.sort((a, b) => b[1] - a[1]) // ordered by the most recent product
			.map(([productKey]) => productKey);
	};

	return { getNewlyConnectedProducts };
};

export const useInitializeBulkConnect = (
	availableProducts: ProductKeys3P[],
	recentlyConnectedProducts: ProductKeys3P[],
): void => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	const { initializeProductState } = useBulkConnectActions();
	const availableProductsAs3P = availableProducts.filter(
		(productKey): productKey is ProductKeys3P =>
			is3pProductKey(productKey) && productKey !== ProductKeys3P.AtlassianWebCrawler,
	);

	useEffect(() => {
		initializeProductState(availableProductsAs3P, recentlyConnectedProducts, thirdPartyConfigs);
	}, [initializeProductState, availableProductsAs3P, recentlyConnectedProducts, thirdPartyConfigs]);
};

export const useBulkConnectState = () => {
	const { setProductStatus, setProductConnectedInSession } = useBulkConnectActions();
	const [productStatuses] = useProductStatuses();
	const [productOrder] = useProductOrder();
	const [productConnectedInSession] = useProductConnectedInSession();
	return {
		productStatuses,
		productOrder,
		productConnectedInSession,
		setProductStatus,
		setProductConnectedInSession,
	};
};

export const useBulkConnectModalOpenState = () => {
	const [isBulkConnectModalOpen] = useIsBulkConnectModalOpen();
	const { setIsBulkConnectModalOpen } = useBulkConnectActions();
	return {
		isBulkConnectModalOpen,
		setIsBulkConnectModalOpen,
	};
};
